import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../shared/services/auth.service';
import { AuthLeftPanelComponent } from '../../shared/components/auth-left-panel/auth-left-panel.component';
import { ResetPasswordRequest } from '../../shared/models/auth.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnauthorizedLayoutComponent } from '../../shared/components/unauthorized-layout/unauthorized-layout.component';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.html',
  styleUrls: ['./reset-password.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    AuthLeftPanelComponent,
    UnauthorizedLayoutComponent
  ]
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading = false;
  isValidatingToken = true;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  tokenMessage: string | null = null;
  resetToken: string = '';
  userEmail: string = '';
  isTokenValid = false;
  tokenExpired = false;
  tokenAlreadyUsed = false;
  passwordStrength: { hasNumber: boolean; hasLetter: boolean; hasSpecial: boolean } = {
    hasNumber: false,
    hasLetter: false,
    hasSpecial: false
  };
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      newPassword: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator.bind(this)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Get token from URL query parameters
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.resetToken = params['token'] || '';
      
      if (!this.resetToken) {
        this.tokenMessage = 'Invalid reset link. Token is missing.';
        this.isValidatingToken = false;
        return;
      }

      // Validate token
      this.validateToken();
    });
  }

  /**
   * Validate reset password token
   */
  private validateToken(): void {
    this.authService.validateResetToken(this.resetToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isValidatingToken = false;
          
          if (response.success) {
            const data = response.data;
            
            // Check if token is already used
            if (data.isAlreadyUsed) {
              this.tokenAlreadyUsed = true;
              this.errorMessage = data.message || 'This reset link has already been used.';
              this.tokenMessage = data.message;
              return;
            }

            // Check if token is expired
            if (data.isExpired) {
              this.tokenExpired = true;
              this.errorMessage = data.message || 'This reset link has expired.';
              this.tokenMessage = data.message;
              return;
            }

            // Check if token is valid
            if (data.isValid) {
              this.isTokenValid = true;
            } else {
              this.errorMessage = data.message || 'Invalid reset token.';
              this.tokenMessage = data.message;
            }
          } else {
            this.errorMessage = response.message || 'Token validation failed.';
            this.tokenMessage = response.message;
          }
        },
        error: (error: any) => {
          this.isValidatingToken = false;
          const errorMsg = error?.message || 'Failed to validate reset token.';
          this.errorMessage = errorMsg;
          this.tokenMessage = errorMsg;
        }
      });
  }

  /**
   * Validator to check password strength
   * Must contain: number, letter, and special character
   */
  private passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const hasNumber = /[0-9]/.test(value);
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':\"\\|,.<>/?]/.test(value);

    // Update strength indicator
    this.passwordStrength = { hasNumber, hasLetter, hasSpecial };

    if (!hasNumber || !hasLetter || !hasSpecial) {
      return {
        weakPassword: {
          hasNumber,
          hasLetter,
          hasSpecial
        }
      };
    }
    return null;
  }

  /**
   * Validator to check if passwords match
   */
  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (!password || !confirmPassword) {
      return null;
    }

    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  /**
   * Submit reset password form
   */
  onSubmit(): void {
    if (this.form.invalid || !this.resetToken) return;

    this.errorMessage = null;
    this.successMessage = null;
    this.isLoading = true;

    const resetRequest: ResetPasswordRequest = {
      resetToken: this.resetToken,
      email: this.form.value.email,
      newPassword: this.form.value.newPassword
    };

    this.authService.resetPassword(resetRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          if (response.success && response.data) {
            this.successMessage = response.data.message || response.message;
            this.form.reset();
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000);
          } else {
            this.errorMessage = response.message || 'Password reset failed.';
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          let errorMsg = error?.message || 'Password reset failed. Please try again.';
          this.errorMessage = errorMsg;
        }
      });
  }

  /**
   * Get error message for field
   */
  getFieldError(fieldName: string): string | null {
    const control = this.form.get(fieldName);
    if (!control || !control.touched) return null;

    if (control.hasError('required')) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }

    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control.hasError('minlength')) {
      return `Password must be at least 8 characters`;
    }

    if (control.hasError('weakPassword')) {
      return 'Password must contain numbers, letters, and special characters';
    }

    return null;
  }

  /**
   * Get error for confirm password field
   */
  getConfirmPasswordError(): string | null {
    const confirmPassword = this.form.get('confirmPassword');
    
    if (!confirmPassword || !confirmPassword.touched) {
      return null;
    }

    if (confirmPassword.hasError('required')) {
      return 'Please confirm your password';
    }

    if (this.form.hasError('passwordMismatch')) {
      return 'Passwords do not match';
    }

    return null;
  }

  /**
   * Navigate back to forgot password page
   */
  goToForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
